import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetClientActivitiesByClientId,
  useGetClientDeliverablesByClientId,
} from "../../../../api/services/client/clientApis";
import { AccordionHeader, AccordionHeaderText } from "../../../../components";
import { MODULE_PERMISSIONS, PAGE_IDS } from "../../../../helpers/constants";
import { isEmpty } from "../../../../helpers/utils";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { setIsDataModifiedCurrentPage } from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { createClientSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateClientActivitiesAndDeliverables } from "./CreateClientActivitiesAndDeliverables";
import { CreateActivity } from "./components/createActivity/CreateActivity";
import { CreateClientDeliverable } from "./components/createDeliverable/CreateDeliverable";

const pageId = PAGE_IDS.CREATE_CLIENT_ACTIVITY_DELIVERABLES;

interface CreateClientActivityAndDeliverableMainProps {
  isAddButtonVisible?: boolean;
  parentAccordionStatus: boolean;
  onAccordionChange: (expanded: boolean) => void;
}

export const CreateClientActivityAndDeliverableMain = ({
  isAddButtonVisible = true,
  parentAccordionStatus,
  onAccordionChange,
}: CreateClientActivityAndDeliverableMainProps) => {
  const { id: clientId } = useSelector(createClientSelector);
  const dispatch = useDispatch();

  const [
    activitiesAndDeliverablesAccordionStatus,
    setActivitiesAndDeliverablesAccordionStatus,
  ] = useState(parentAccordionStatus);

  useEffect(() => {
    setActivitiesAndDeliverablesAccordionStatus(parentAccordionStatus);
  }, [parentAccordionStatus]);

  const [isOpenActivity, setIsOpenActivity] = useState(false);
  const [isOpenDeliverable, setIsOpenDeliverable] = useState(false);
  const { fetchComponentTitle } = useSiteCore();
  const { hasModulePermission } = usePermissions();
  const {
    data: listOfActivitiesData,
    refetchWithParams: getClientActivity,
    refetch: refetchClientActivity,
  } = useGetClientActivitiesByClientId();

  const {
    data: listOfDeliverablesData,
    refetch: refetchDeliverables,
    refetchWithParams: getClientDeliverables,
  } = useGetClientDeliverablesByClientId();

  useEffect(() => {
    if (isEmpty(clientId)) return;
    getClientActivity(clientId);
    getClientDeliverables(clientId);
  }, [clientId]);

  const handleActivitiesAndDeliverablesChange = (
    _event: React.SyntheticEvent,
    expanded: boolean,
  ) => {
    setActivitiesAndDeliverablesAccordionStatus(expanded);
    onAccordionChange(expanded);
  };

  const onCreateDeliverableClick = () => {
    setIsOpenDeliverable(true);
  };

  const onCreateActivityClick = () => {
    hasModulePermission(MODULE_PERMISSIONS.ADD_CLIENT_SPECIFIC_ACTIVITIES) &&
      setIsOpenActivity(true);
  };

  return (
    <AccordionHeader
      title={
        <AccordionHeaderText
          label={fetchComponentTitle(
            createClientSiteCoreKeys.clientSpecificActivitiesAndDeliverables,
          )}
        />
      }
      onChange={handleActivitiesAndDeliverablesChange}
      isExpanded={activitiesAndDeliverablesAccordionStatus}
    >
      <CreateClientActivitiesAndDeliverables
        rows={listOfActivitiesData ?? []}
        deliverableRows={listOfDeliverablesData ?? []}
        isView={!isAddButtonVisible}
        onCreateActivityClick={onCreateActivityClick}
        successHandler={refetchClientActivity}
        successHandlerDeliverables={refetchDeliverables}
        onCreateDeliverableClick={onCreateDeliverableClick}
      />
      {isOpenActivity && (
        <CreateActivity
          show={isOpenActivity}
          handleCloseModal={() => setIsOpenActivity(false)}
          pageId={pageId}
          getListOfActivitiesApi={() => {
            dispatch(setIsDataModifiedCurrentPage(true));
            refetchClientActivity();
          }}
        />
      )}

      {isOpenDeliverable && (
        <CreateClientDeliverable
          show={isOpenDeliverable}
          handleCloseModal={() => setIsOpenDeliverable(false)}
          pageId={pageId}
          getListOfDeliverablesApi={() => {
            dispatch(setIsDataModifiedCurrentPage(true));
            refetchDeliverables();
          }}
        />
      )}
    </AccordionHeader>
  );
};
